import { Alert, Flex, Group, Loader } from "@mantine/core";
import React, { useEffect } from "react";
import { VSpace } from "../../../components";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { selectCurrentProjectId } from "../../../global/globalSliceCommon";
import { MCApplicationsUrlEnum } from "@mcapps/shared/sharedConstants";
import { useGetProjectQuery } from "../../projectRTK";
import { doLogUsage } from "../admin/restQueries";
import ChecklistAdminDepricated from "./ChecklistAdminDepricated";
import ChecklistBoardAgGridDepricated from "./ChecklistBoardAgGridDepricated";
import { ChecklistLogsTableDepricated } from "./ChecklistLogsDepricated";
import ChecklistAppRefactored from "./ChecklistRefactor/ChecklistAppRefactored";
import { getChecklistThunk, setActiveChecklistTabInRedux, setCurrentChecklistInRedux } from "./checklistSlice";
import {
  selectChecklistActiveTab,
  selectActiveChecklist,
  selectChecklistCategoriesDepricated,
  selectChecklistDepricated,
  selectChecklistHiddenColumns,
  selectChecklistItemsDepricated,
  selectEffectiveChecklistTableVariant,
} from "./checklistSelectors";
import ChecklistTabs from "./ChecklistTabs";
import { ChecklistStatusEnum } from "./constants";
import DoksamChecklistBanner from "./doksam/DoksamChecklistBanner";

const ChecklistApp: React.FC<{ checklistId: string }> = ({ checklistId }) => {
  const dispatch = useAppDispatch();
  const projectId = useAppSelector(selectCurrentProjectId);
  const effectiveChecklistTableVariant = useAppSelector(selectEffectiveChecklistTableVariant);
  const { data: project } = useGetProjectQuery(projectId ?? "", { skip: projectId == null });

  useEffect(() => {
    if (project == null) {
      return;
    }

    doLogUsage(project, MCApplicationsUrlEnum.CHECKLIST, "open");
  }, [project]);

  useEffect(() => {
    return () => {
      dispatch(setActiveChecklistTabInRedux("CHECKLIST"));
      dispatch(setCurrentChecklistInRedux(null));
    };
  }, [checklistId, dispatch]);

  if (effectiveChecklistTableVariant === "legacy") {
    return <ChecklistAppDepricated checklistId={checklistId} project={project} />;
  }

  return <ChecklistAppRefactoredShell checklistId={checklistId} project={project} />;
};

const ChecklistAppDepricated: React.FC<{
  checklistId: string;
  project: ReturnType<typeof useGetProjectQuery>["data"];
}> = ({ checklistId, project }) => {
  const dispatch = useAppDispatch();
  const checklist = useAppSelector(selectChecklistDepricated);
  const checklistCategories = useAppSelector(selectChecklistCategoriesDepricated);
  const checklistItems = useAppSelector(selectChecklistItemsDepricated);
  const activeTab = useAppSelector(selectChecklistActiveTab);
  const hiddenColumns = useAppSelector(selectChecklistHiddenColumns);

  useEffect(() => {
    dispatch(getChecklistThunk(checklistId));
  }, [checklistId, dispatch]);

  if (checklist == null) {
    return (
      <Group justify="center" flex={1} p="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <>
      {checklist?.status !== ChecklistStatusEnum.REMOVED ? (
        <Flex direction="column" w="100%" h="100%" style={{ minHeight: 0, overflow: "hidden" }} gap="0">
          {project?.maconomy?.projectNo === 100 && (
            <Alert mt="sm" color="yellow" variant="light">
              Denne sjekklisten vil være tilgjengelig som mal for alle prosjekter!
            </Alert>
          )}
          {checklist?.doksam?.docId != null && <DoksamChecklistBanner checklist={checklist} />}
          <ChecklistTabs checklist={checklist} />
          {activeTab === "CHECKLIST" && checklist != null && (
            <ChecklistBoardAgGridDepricated
              checklist={checklist}
              checklistCategories={checklistCategories}
              checklistItems={checklistItems}
              hiddenColumns={hiddenColumns}
            />
          )}
          {activeTab === "LOGS" && <ChecklistLogsTableDepricated />}
          {activeTab === "SETTINGS" && <ChecklistAdminDepricated />}
        </Flex>
      ) : checklist?.isTemplate === true ? (
        "Mal slettet!"
      ) : (
        "Sjekkliste slettet!"
      )}
    </>
  );
};

const ChecklistAppRefactoredShell: React.FC<{
  checklistId: string;
  project: ReturnType<typeof useGetProjectQuery>["data"];
}> = ({ checklistId, project }) => {
  const checklist = useAppSelector((state) => selectActiveChecklist(state, checklistId));

  if (checklist?.status === ChecklistStatusEnum.REMOVED) {
    return checklist?.isTemplate === true ? <>Mal slettet!</> : <>Sjekkliste slettet!</>;
  }

  return (
    <Flex direction="column" w="100%" h="100%" style={{ minHeight: 0, overflow: "hidden" }}>
      {project?.maconomy?.projectNo === 100 && (
        <Alert mt="sm" color="yellow" variant="light">
          Denne sjekklisten vil vÃ¦re tilgjengelig som mal for alle prosjekter!
        </Alert>
      )}
      {checklist?.doksam?.docId != null && <DoksamChecklistBanner checklist={checklist} />}
      <ChecklistTabs />
      <VSpace />
      <ChecklistAppRefactored />
    </Flex>
  );
};

export default ChecklistApp;
